'use client';

import React, { useState, useEffect } from 'react';
import { useParams, useRouter } from 'next/navigation';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/Footer';
import { db, requireDb } from '@/lib/firebase';
import { doc, getDoc, collection, addDoc, getDocs, query, orderBy, deleteDoc, where } from 'firebase/firestore';
import { getCache, setCache } from '@/lib/cache';
import { useAuthContext } from '@/lib/AuthContext';
import { useDarkMode } from '@/lib/DarkModeContext';
import { Article } from '@/types/article';
import useTrackArticleView from '@/hooks/useTrackArticleView';
import ShareButtons from '@/components/common/ShareButtons';
import NewsletterSubscribeWidget from '@/components/common/NewsletterSubscribeWidget';
import SupportUs from '@/components/common/SupportUs';
import { generateSlug } from '@/lib/slugs';
import { useSiteLanguage } from '@/lib/SiteLanguageContext';

export default function ArticleClient() {
    const params = useParams();
    const router = useRouter();
    const { user } = useAuthContext();
    const { darkMode } = useDarkMode();
    const { siteLang } = useSiteLanguage();

    const categoryTranslations: Record<string, string> = {
        'Vijesti': 'News', 'Naoru\u017eanje': 'Armament', 'Municija': 'Ammunition',
        'Namjenska preduze\u0107a': 'Defense Enterprises', 'Vojna istorija': 'Military History',
        'Kopnene snage': 'Ground Forces', 'Vazduhoplovstvo i PVO': 'Air Force & Air Defense',
        'Mornarica': 'Navy', 'Strate\u0161ke snage': 'Strategic Forces',
        'Naoru\u017eanje Tre\u0107eg rajha': 'Third Reich Armaments', 'Naoru\u017eanje biv\u0161e JNA': 'Former JNA Armaments',
        'Savremeno naoru\u017eanje': 'Modern Armaments', 'Konstrukcija naoru\u017eanja': 'Weapon Construction',
        'Tenkovi i oklopna vozila': 'Tanks & Armored Vehicles', 'Artiljerija': 'Artillery',
        'Samostreli': 'Crossbows', 'Pi\u0161tolji': 'Pistols', 'Revolveri': 'Revolvers',
        'Automati': 'Submachine Guns', 'Pu\u0161ke': 'Rifles', 'Karabini': 'Carbines',
        'Pu\u0161komitraljezi': 'Light Machine Guns', 'Mitraljezi': 'Machine Guns',
        'Ru\u010dni baca\u010di': 'Hand Launchers', 'Ru\u010dni raketni baca\u010di': 'Rocket Launchers',
        'Baca\u010di granata': 'Grenade Launchers', 'Trombloni': 'Rifle Grenade Launchers',
        'Avioni i helikopteri': 'Aircraft & Helicopters', 'PVO sistemi i raketno naoru\u017eanje': 'Air Defense & Missile Systems',
        'Bespilotne letjelice': 'Unmanned Aerial Vehicles (UAV)',
        'Površinska plovila': 'Surface Vessels', 'Podmornica': 'Submarines',
        'Mornarničko naoružanje': 'Naval Weapons', 'Mornaricko naoružanje': 'Naval Weapons', 'Mornaričko naoružanje': 'Naval Weapons',
        'Pje\u0161adijska municija': 'Infantry Ammunition', 'Minobaca\u010dka municija': 'Mortar Ammunition',
        'Klasi\u010dna artiljerijska municija': 'Classic Artillery Ammunition',
        'Raketna artiljerijska municija': 'Rocket Artillery Ammunition',
        'Vodjene rakete': 'Guided Missiles', 'Ru\u010dne bombe': 'Hand Grenades',
        'Mine za ru\u010dne baca\u010de': 'Grenades for Handheld Launchers',
        'Municija za bestrzajne topove': 'Recoilless Cannon Ammunition',
        'Balistika': 'Ballistics', 'Savremena municija': 'Modern Ammunition',
        'Eksplozivne materije': 'Explosives', 'Elementi municije': 'Ammunition Elements',
        'Pirotehnika': 'Pyrotechnics', 'Upalja\u010di': 'Fuzes', 'Upalja\u010dki': 'Fuzes',
        'Istorija razvoja': 'History of Development', 'Proizvodni kapaciteti': 'Production Capacity',
        'Stari jugoslovenski projekti': 'Old Yugoslav Projects',
        'Modernizacija i izvozni program': 'Modernization & Export Program',
        'Zemlje biv\u0161e Jugoslavije': 'Former Yugoslav Countries',
        'Evropa': 'Europe', 'NATO': 'NATO', 'Rusija': 'Russia',
        'Bliski istok': 'Middle East', 'Rat u Ukrajini': 'War in Ukraine',
        'Vojske kroz vijekove': 'Armies Through the Ages',
        'I svjetski rat': 'World War I', 'II svjetski rat': 'World War II',
        'Hladni rat': 'Cold War', 'Poznati konstruktori': 'Famous Designers',
        'Nekategorisano': 'Uncategorized',
        'Strateško naoružanje': 'Strategic Armaments',
    };
    const displayRenames: Record<string, string> = {
        'Strateške snage': 'Strateško naoružanje',
    };
    const translateCat = (name?: string) => {
        if (!name) return '';
        const display = displayRenames[name] ?? name;
        return siteLang === 'en' ? (categoryTranslations[display] ?? categoryTranslations[name] ?? display) : display;
    };
    const id = params.id as string;
    const [article, setArticle] = useState<Article | null>(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    // Track article view when article is loaded
    useTrackArticleView(article || undefined);

    interface Comment {
        docId: string;
        tekst: string;
        autor: string;
        autorEmail?: string;
        datum: string;
        vrijeme?: string;
        userId?: string;
    }
    const [comments, setComments] = useState<Comment[]>([]);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [commentText, setCommentText] = useState('');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [commentAuthor, setCommentAuthor] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);

    const loadComments = async (docId: string) => {
        try {
            setCommentsLoading(true);
            const commentsRef = collection(requireDb(), 'clanci', docId, 'comments');
            const q = query(commentsRef, orderBy('datum', 'desc'));
            const querySnapshot = await getDocs(q);
            const allComments: Comment[] = [];
            querySnapshot.docs.forEach((docSnap) => {
                allComments.push({
                    ...docSnap.data() as Omit<Comment, 'docId'>,
                    docId: docSnap.id
                });
            });
            setComments(allComments);
        } catch {
            // Error loading comments
        } finally {
            setCommentsLoading(false);
        }
    };

    useEffect(() => {
        const loadArticle = async () => {
            try {
                window.scrollTo(0, 0);
                const TTL = 60 * 60 * 1000; // 1 hour
                const cacheKey = `article:${id}`;

                // Check cache first
                const cached = getCache<Article>(cacheKey);
                if (cached) {
                    setArticle(cached);
                    setLoading(false);
                    // Load comments using the cached article's docId
                    await loadComments(cached.docId);
                    return;
                }

                // No cache — try multiple lookup strategies
                let a: Article | null = null;

                // Strategy 1: Try stored slug field lookup (new articles with persisted slug)
                {
                    console.log('[Article Loader] Attempting slug field lookup for:', id);
                    try {
                        const slugQuery = query(collection(requireDb(), 'clanci'), where('slug', '==', id));
                        const slugSnapshot = await getDocs(slugQuery);
                        if (!slugSnapshot.empty) {
                            const docSnap = slugSnapshot.docs[0];
                            console.log('[Article Loader] SLUG FIELD MATCH FOUND');
                            a = {
                                docId: docSnap.id,
                                ...docSnap.data()
                            } as unknown as Article;
                        }
                    } catch (error) {
                        console.error('[Article Loader] Error in slug field lookup:', error);
                    }
                }

                // Strategy 2: Try matching via generateSlug(naslov) (old articles without stored slug)
                if (!a) {
                    console.log('[Article Loader] Attempting title-based slug lookup for:', id);
                    try {
                        const allArticles = await getDocs(collection(requireDb(), 'clanci'));
                        for (const doc of allArticles.docs) {
                            const data = doc.data() as Article;
                            if (data.naslov) {
                                const articleSlug = generateSlug(data.naslov);
                                if (articleSlug === id) {
                                    console.log('[Article Loader] SLUG MATCH FOUND via title:', data.naslov);
                                    a = {
                                        docId: doc.id,
                                        ...data
                                    } as unknown as Article;
                                    break;
                                }
                            }
                        }
                    } catch (error) {
                        console.error('[Article Loader] Error in title slug lookup:', error);
                    }
                }

                // Strategy 3: Try searching by numeric 'id' field (OLD ARTICLES)
                if (!a) {
                    try {
                        const numId = parseInt(id, 10);
                        if (!isNaN(numId)) {
                            console.log('[Article Loader] Attempting numeric ID lookup for:', numId);
                            const q = query(collection(requireDb(), 'clanci'), where('id', '==', numId));
                            const snapshot = await getDocs(q);
                            if (!snapshot.empty) {
                                console.log('[Article Loader] Found by numeric ID');
                                const docSnap = snapshot.docs[0];
                                a = {
                                    docId: docSnap.id,
                                    ...docSnap.data()
                                } as unknown as Article;
                            }
                        }
                    } catch (error) {
                        console.error('[Article Loader] Error in numeric ID lookup:', error);
                    }
                }

                // Strategy 3: Try fetching directly by docId (OLD ARTICLES)
                if (!a) {
                    try {
                        console.log('[Article Loader] Attempting docId lookup for:', id);
                        const docSnapshot = await getDoc(doc(requireDb(), 'clanci', id));
                        if (docSnapshot.exists()) {
                            console.log('[Article Loader] Found by docId');
                            a = {
                                docId: docSnapshot.id,
                                ...docSnapshot.data()
                            } as unknown as Article;
                        }
                    } catch (error) {
                        console.error('[Article Loader] Error in docId lookup:', error);
                    }
                }

                if (a) {
                    console.log('[Article Loader] SUCCESS: Article loaded -', a.naslov);
                    setArticle(a);
                    setCache(cacheKey, a, TTL);
                    // Load comments using the article's docId
                    await loadComments(a.docId);
                } else {
                    console.log('[Article Loader] FAILED: Article not found for ID:', id);
                    setNotFound(true);
                }
            } catch (error) {
                console.error('[Article Loader] Unexpected error:', error);
                setNotFound(true);
            } finally {
                setLoading(false);
            }
        };

        loadArticle();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleAddComment = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!user) {
            alert(siteLang === 'en' ? 'Please log in to comment' : 'Molimo prijavite se da biste komentarisali');
            router.push('/login');
            return;
        }

        if (!commentText.trim()) {
            alert(siteLang === 'en' ? 'Please enter a comment' : 'Molimo unesite komentar');
            return;
        }

        try {
            setSubmittingComment(true);
            const commentsRef = collection(requireDb(), 'clanci', article!.docId, 'comments');
            await addDoc(commentsRef, {
                autor: user.displayName || user.email || 'Anonimno',
                autorEmail: user.email,
                tekst: commentText,
                datum: new Date().toLocaleDateString('sr-Latn-RS', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                }),
                vrijeme: new Date().toLocaleTimeString('sr-Latn-RS', {
                    hour: '2-digit',
                    minute: '2-digit'
                })
            });
            setCommentText('');
            setCommentAuthor('');
            await loadComments(article!.docId);
        } catch {
            alert(siteLang === 'en' ? 'Error sending comment' : 'Greška pri slanju komentara');
        } finally {
            setSubmittingComment(false);
        }
    };

    const handleDeleteComment = async (commentDocId: string) => {
        if (confirm(siteLang === 'en' ? 'Are you sure you want to delete this comment?' : 'Sigurno želite obrisati komentar?')) {
            try {
                await deleteDoc(doc(requireDb(), 'clanci', article!.docId, 'comments', commentDocId));
                await loadComments(article!.docId);
            } catch {
                // Error deleting comment
            }
        }
    };

    // Lightweight formatter: supports **bold** markers stored in article text
    const escapeHtml = (str: string) => {
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    };

    const formatContentToHtml = (text: string) => {
        if (!text) return '';

        // Store links in array with numeric placeholders (won't get escaped)
        const links: Array<{ text: string; url: string }> = [];
        let textWithPlaceholders = text.replace(/\[(.+?)\]\((.+?)\)/g, (match, linkText, linkUrl) => {
            links.push({ text: linkText, url: linkUrl });
            return `{LINK${links.length - 1}}`;
        });

        // Now escape HTML
        const escaped = escapeHtml(textWithPlaceholders);

        // Apply bold formatting (dotall not available on older targets; use [\s\S] to match across lines)
        let processed = escaped.replace(/\*\*([\s\S]+?)\*\*/g, '<strong>$1</strong>');

        // Replace link placeholders with actual anchor tags
        links.forEach((link, index) => {
            const placeholder = `{LINK${index}}`;
            processed = processed.replace(placeholder, `<a href="${link.url}" class="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">${link.text}</a>`);
        });

        // Convert paragraphs and line breaks — handle CRLF (Windows) and preserve empty lines
        const paragraphs = processed.split(/\r?\n\s*\r?\n/).map(p => p.trim()).filter(Boolean).map(p => p.replace(/\r?\n/g, '<br/>'));
        return paragraphs.map(p => `<p>${p}</p>`).join('');
    };

    if (loading) {
        return (
            <main className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
                <Header />
                <section className="mt-16 py-12">
                    <div className="max-w-5xl mx-auto px-4 text-center">
                        <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{siteLang === 'en' ? 'Loading...' : 'Učitavanje...'}</p>
                    </div>
                </section>
                <Footer />
            </main>
        );
    }
                        {/* Support Us Section */}
                        <section className="py-8" style={{ backgroundColor: darkMode ? "#1f2937" : "#f3f4f6" }}>
                            <div className="max-w-5xl mx-auto px-4">
                                <SupportUs />
                            </div>
                        </section>

    if (notFound) {
        return (
            <main className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
                <Header />
                <section className="mt-16 py-12">
                    <div className="max-w-5xl mx-auto px-4 text-center">
                        <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-lg mb-6`}>{siteLang === 'en' ? 'Article not found' : 'Članak nije pronađen'}</p>
                        <button
                            onClick={() => router.back()}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            {siteLang === 'en' ? 'Back' : 'Nazad'}
                        </button>
                    </div>
                </section>
                <Footer />
            </main>
        );
    }

    if (!article) {
        return null;
    }

    // Navigation layout helpers: ensure next link sits on the right even if prev is missing
    const hasPrev = Boolean(article.prevLink && article.prevLink.trim());
    const hasNext = Boolean(article.nextLink && article.nextLink.trim());
    const navJustify = hasPrev && hasNext ? 'justify-between' : hasNext ? 'justify-end' : 'justify-start';

    return (
        <main className={`min-h-screen no-top-gap ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
            <Header />

            {/* Hero Section */}
            <section className="mt-0 pt-4 pb-2 md:pt-24 md:pb-4 px-4 lg:px-[calc(1rem+180px)] bg-[#111827]">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center">
                        {/* Breadcrumb Categories */}
                        <p className="text-sm sm:text-base text-white font-medium mb-4">
                            {article.kategorija === 'Vijesti' ? translateCat('Vijesti') : translateCat(article.podkategorija || article.kategorija)}
                            {article.kategorija !== 'Vijesti' && article.podpodkategorija && (
                                <>
                                    <span className="text-gray-400 mx-3">›</span>
                                    {translateCat(article.podpodkategorija)}
                                </>
                            )}
                        </p>

                        <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">{article.naslov}</h1>

                        <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-200 mb-6 max-w-2xl mx-auto">
                            {article.kratak_opis}
                        </p>

                        <p className="text-xs sm:text-base text-gray-300">
                            {siteLang === 'en' ? 'Author' : 'Autor'}: {article.autor} • {article.datum}
                        </p>

                        {/* Language switcher */}
                        {article.lang_pair_slug && (
                            <div className="mt-4">
                                <a
                                    href={`/article/${article.lang_pair_slug}`}
                                    className="inline-block px-5 py-2 border border-white/40 rounded-full text-sm font-semibold text-white hover:bg-white/10 transition-colors"
                                >
                                    {article.lang === 'en' ? '🇷🇸 Čitaj na srpskom' : '🇬🇧 Read in English'}
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Article Image - connected to hero */}
            {article.naslovna_slika && (
                <div className="w-full">
                    <img
                        src={article.naslovna_slika}
                        alt={article.naslov}
                        className="w-full h-auto object-contain mb-8"
                        onError={(e) => {
                            e.currentTarget.style.display = 'none';
                        }}
                    />
                </div>
            )}

            {/* Newsletter Section */}
            <section className="py-8" style={{ backgroundColor: darkMode ? "#1f2937" : "#f3f4f6" }}>
                <div className="max-w-5xl mx-auto px-4">
                    <NewsletterSubscribeWidget />
                </div>
            </section>

            {/* Article Header */}
            <section className="py-6">
                <div className="max-w-5xl mx-auto px-4">
                    {/* Sadržaj članka */}
                    <div className={`${darkMode ? 'text-gray-300' : 'text-gray-800'} leading-relaxed text-lg`}>
                        {/** Helper to safely render our lightweight formatting **/}
                        {/** Formatting markers supported: **bold** and ==highlight== **/}
                        {Array.isArray(article.detaljan_opis) ? (
                            // Prikazuj kao blokove (tekst + slike)
                            <div className="space-y-6">
                                {article.detaljan_opis.map((block, index) => (
                                    <div key={index}>
                                        {block.type === 'text' ? (
                                            <div className="space-y-4" dangerouslySetInnerHTML={{ __html: formatContentToHtml(block.value) }} />
                                        ) : block.type === 'table' && block.tableData ? (
                                            (() => {
                                                const td = block.tableData!;
                                                const cells: string[][] = td.cells ?? Array.from(
                                                    { length: td.rows },
                                                    (_r, r) => Array.from({ length: td.cols }, (_c, c) => td.cellsFlat?.[r * td.cols + c] ?? '')
                                                );
                                                return (
                                                    <div className="overflow-x-auto my-6">
                                                        <table className={`w-full border-collapse text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                                            <thead>
                                                                <tr>
                                                                    {cells[0]?.map((cell, colIndex) => (
                                                                        <th key={colIndex} className={`border px-4 py-2 text-left font-semibold ${darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-100'}`}>
                                                                            {cell}
                                                                        </th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {cells.slice(1).map((row, rowIndex) => (
                                                                    <tr key={rowIndex} className={rowIndex % 2 === 0 ? (darkMode ? 'bg-gray-800' : 'bg-white') : (darkMode ? 'bg-gray-900' : 'bg-gray-50')}>
                                                                        {row.map((cell, colIndex) => (
                                                                            <td key={colIndex} className={`border px-4 py-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                                                                                {cell}
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                );
                                            })()
                                        ) : (
                                            <figure className="my-8">
                                                <img
                                                    src={block.value}
                                                    alt={block.imageCaption || 'Slika u članku'}
                                                    className="w-full h-auto object-contain rounded-lg"
                                                    onError={(e) => {
                                                        e.currentTarget.style.display = 'none';
                                                    }}
                                                />
                                                {block.imageCaption && (
                                                    <figcaption className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'} text-sm mt-2 italic`}>
                                                        {block.imageCaption}
                                                    </figcaption>
                                                )}
                                            </figure>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            // Prikazuj kao obični tekst (nazad kompatibilnost)
                            <div className="space-y-6" dangerouslySetInnerHTML={{ __html: formatContentToHtml(String(article.detaljan_opis || '')) }} />
                        )}
                    </div>
                </div>
            </section>


            {/* Navigation Links (optional) */}
            {(article.prevLink && article.prevLink.trim()) || (article.nextLink && article.nextLink.trim()) ? (
                <section className="py-6">
                    <div className={`max-w-5xl mx-auto px-4 flex justify-between items-start gap-8`}>
                        {article.prevLink && article.prevLink.trim() && (
                            <a href={article.prevLink} className="text-blue-600 font-semibold hover:underline flex items-start gap-2 group flex-1">
                                <span aria-hidden className="mt-1 flex-shrink-0">←</span>
                                <div className="flex flex-col">
                        <span>{siteLang === 'en' ? 'Previous article' : 'Prethodni članak'}</span>
                                    {article.prevTitle && <span className="font-normal text-gray-700 dark:text-gray-300 group-hover:text-blue-700">{article.prevTitle}</span>}
                                </div>
                            </a>
                        )}

                        {article.nextLink && article.nextLink.trim() && (
                            <a href={article.nextLink} className="text-blue-600 font-semibold hover:underline flex items-start gap-2 group justify-end text-right flex-1">
                                <div className="flex flex-col">
                                    <span>{siteLang === 'en' ? 'Next article' : 'Sljedeći članak'}</span>
                                    {article.nextTitle && <span className="font-normal text-gray-700 dark:text-gray-300 group-hover:text-blue-700">{article.nextTitle}</span>}
                                </div>
                                <span aria-hidden className="mt-1 flex-shrink-0">→</span>
                            </a>
                        )}
                    </div>
                </section>
            ) : null}

            {/* Support Us Section (moved above Share) */}
            <section className="py-8" style={{ backgroundColor: darkMode ? "#1f2937" : "#f3f4f6" }}>
                <div className="max-w-5xl mx-auto px-4">
                    <SupportUs />
                </div>
            </section>

            {/* Share / Social buttons */}
            <section className="py-6">
                <div className="max-w-5xl mx-auto px-4">
                    <h2 className={`text-2xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{siteLang === 'en' ? 'Share article' : 'Podeli članak'}</h2>
                    <ShareButtons title={article.naslov} image={article.naslovna_slika} />
                </div>
            </section>

            {/* Comments Section */}
            <section className={`py-12 ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <div className="max-w-5xl mx-auto px-4">
                    <h2 className={`text-3xl font-bold mb-8 ${darkMode ? 'text-white' : 'text-black'}`}>{siteLang === 'en' ? 'Comments' : 'Komentari'} ({comments.length})</h2>

                    {/* Add Comment Form */}
                    {user ? (
                        <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} rounded-lg p-6 mb-8 border`}>
                            <form onSubmit={handleAddComment}>
                                <div className="mb-4">
                                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>{siteLang === 'en' ? 'Your name' : 'Vaše ime'}</label>
                                    <p className={`w-full px-4 py-2 border rounded-lg ${darkMode ? 'border-gray-600 bg-gray-600 text-gray-200' : 'border-gray-300 bg-gray-100 text-gray-700'}`}>
                                        {user.displayName || user.email}
                                    </p>
                                </div>
                                <div className="mb-4">
                                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>{siteLang === 'en' ? 'Comment' : 'Komentar'}</label>
                                    <textarea
                                        value={commentText}
                                        onChange={(e) => setCommentText(e.target.value)}
                                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-600 border-gray-600 text-white' : 'border-gray-300 text-black'}`}
                                        placeholder={siteLang === 'en' ? 'Enter your comment...' : 'Unesite vaš komentar...'}
                                        rows={4}
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={submittingComment}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:bg-gray-400"
                                >
                                    {submittingComment ? (siteLang === 'en' ? 'Sending...' : 'Slanje...') : (siteLang === 'en' ? 'Post comment' : 'Pošalji komentar')}
                                </button>
                            </form>
                        </div>
                    ) : (
                        <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-blue-50 border-blue-200'} rounded-lg p-6 mb-8 border`}>
                            <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-4`}>{siteLang === 'en' ? 'Please log in to comment' : 'Molimo prijavite se da biste komentarisali'}</p>
                            <button
                                onClick={() => router.push('/login')}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                            >
                                {siteLang === 'en' ? 'Log in' : 'Prijavite se'}
                            </button>
                        </div>
                    )}

                    {/* Comments List */}
                    {commentsLoading ? (
                        <div className="text-center py-8">
                            <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{siteLang === 'en' ? 'Loading comments...' : 'Učitavanje komentara...'}</p>
                        </div>
                    ) : comments.length === 0 ? (
                        <div className={`text-center py-8 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} rounded-lg border`}>
                            <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{siteLang === 'en' ? 'No comments yet. Be the first to comment!' : 'Nema komentara. Budite prvi koji će komentarisati!'}</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {comments.map((comment) => (
                                <div key={comment.docId} className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} rounded-lg p-6 border`}>
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <h4 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{comment.autor}</h4>
                                            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{comment.datum} • {comment.vrijeme}</p>
                                        </div>
                                        {user && (user.displayName === comment.autor || user.email === comment.autorEmail) && (
                                            <button
                                                onClick={() => handleDeleteComment(comment.docId)}
                                                className="px-3 py-1 text-xs text-white rounded hover:opacity-90 transition-colors"
                                                style={{ backgroundColor: '#B91C1C' }}
                                            >
                                                {siteLang === 'en' ? 'Delete' : 'Obriši'}
                                            </button>
                                        )}
                                    </div>
                                    <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{comment.tekst}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>



            <div className="mt-auto">
                <Footer />
            </div>
        </main>
    );
}